import { useState, useEffect } from "react";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { useOverallScores } from "../../hooks/useOverallScores";
import { useAwards } from "../../hooks/useAwards";
import { SupplierAwardCard } from "./SupplierAwardCard";
import { getLeaderInviteId, getLeaderScore, isBelowThreshold } from "./utils";
import type { AwardStatus } from "./SupplierAwardCard";

interface AwardState {
  award_status: AwardStatus;
  comment: string;
}

interface OverallAwardViewProps {
  rfxId: number;
  evalId: number;
}

export const OverallAwardView: React.FC<OverallAwardViewProps> = ({
  rfxId,
  evalId,
}) => {
  const { data: overallData, isLoading: scoresLoading } = useOverallScores(rfxId, evalId);
  const { awardsData, isLoading: awardsLoading, upsertAwards } = useAwards(rfxId, evalId);

  const [awardStates, setAwardStates] = useState<Record<number, AwardState>>({});
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && awardsData && overallData) {
      const initial: Record<number, AwardState> = {};
      for (const supplier of overallData.suppliers) {
        const existing = awardsData.awards.find(
          (a) => a.invite_id === supplier.invite_id
        );
        initial[supplier.invite_id] = {
          award_status: existing?.award_status ?? "pending",
          comment: existing?.comment ?? "",
        };
      }
      setAwardStates(initial);
      setInitialized(true);
    }
  }, [awardsData, overallData, initialized]);

  const handleAwardStatusChange = (inviteId: number, status: AwardStatus) => {
    setAwardStates((prev) => {
      const next: Record<number, AwardState> = { ...prev };

      // When awarding a supplier, default all others to unsuccessful
      if (status === "awarded") {
        for (const id of Object.keys(next)) {
          const numId = Number(id);
          if (numId !== inviteId) {
            next[numId] = { ...next[numId], award_status: "unsuccessful" };
          }
        }
      }

      next[inviteId] = { ...next[inviteId], award_status: status };
      return next;
    });
  };

  const handleCommentChange = (inviteId: number, comment: string) => {
    setAwardStates((prev) => ({
      ...prev,
      [inviteId]: { ...prev[inviteId], comment },
    }));
  };

  const handleSave = () => {
    const decisions = Object.entries(awardStates).map(([inviteId, state]) => ({
      rfx_invite_id: Number(inviteId),
      award_status: state.award_status,
      comment: state.comment || null,
    }));
    upsertAwards.mutate({ decisions });
  };

  const isLoading = scoresLoading || awardsLoading;

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="overall-award-view-loading">
        {[1, 2, 3].map((i) => (
          <SkeletonBase
            key={i}
            startColor="lightgray"
            width="100%"
            height="120px"
            className="!rounded-lg"
          />
        ))}
      </div>
    );
  }

  if (!overallData) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm">
        Overall scores are not yet available.
      </div>
    );
  }

  const suppliers = overallData.suppliers;
  const leaderId = getLeaderInviteId(suppliers);
  const leaderScore = getLeaderScore(suppliers, leaderId);

  const canFinalize = awardsData?.can_finalize ?? false;
  const isSaving = upsertAwards.isPending;

  return (
    <div className="space-y-4" data-testid="overall-award-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Award Decisions
          </h2>
          {!overallData.is_scoring_complete && (
            <p className="text-xs text-amber-600 mt-0.5">
              Consensus scoring is not complete — some scores may be pre-meeting averages.
            </p>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {suppliers.map((supplier) => (
          <SupplierAwardCard
            key={supplier.invite_id}
            supplier={supplier}
            awardStatus={awardStates[supplier.invite_id]?.award_status ?? "pending"}
            comment={awardStates[supplier.invite_id]?.comment ?? ""}
            isLeader={supplier.invite_id === leaderId}
            isBelowThreshold={isBelowThreshold(supplier, leaderScore)}
            showAwardControls
            onAwardStatusChange={handleAwardStatusChange}
            onCommentChange={handleCommentChange}
          />
        ))}
      </div>

      <div className="flex justify-end pt-2">
        <Button
          btntype="primary"
          type="button"
          onClick={handleSave}
          disabled={!canFinalize || isSaving}
          isLoading={isSaving}
          data-testid="save-awards-button"
        >
          Save Award Decisions
        </Button>
      </div>

      {upsertAwards.isError && (
        <p className="text-sm text-red-600" role="alert">
          Failed to save award decisions. Please try again.
        </p>
      )}
    </div>
  );
};
